import { uuidV4 } from '@animoto/utils/user';

const HEARTBEAT_MS = 30000;
// Headroom for timeupdate jitter. Real jumps are caught by the seeking guard or the rate bound.
const SAMPLE_SLACK_MS = 500;
const REPORT_VERSION = 1;

export function createSessionId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return uuidV4().toLowerCase();
}

function guard(fn) {
  return (...args) => {
    try {
      return fn(...args);
    } catch (e) {
      // A reporting bug must never break playback.
      return undefined;
    }
  };
}

// A string body makes sendBeacon use text/plain, which is CORS-simple: no preflight at pagehide,
// even from an embed on a customer's domain. sendBeacon returns false when the browser will not
// queue the beacon (a full unload queue, an oversized body), so that falls through to fetch too.
const send = guard((endpoint, payload) => {
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(endpoint, body)) {
      return;
    }
  } catch (e) {
    // A beacon that throws still leaves the fetch fallback.
  }
  if (typeof window.fetch !== 'function') {
    return;
  }
  window.fetch(endpoint, {
    body,
    headers   : { 'Content-Type' : 'text/plain;charset=UTF-8' },
    keepalive : true,
    method    : 'POST'
  }).catch(() => {});
});

function isKey(value) {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Whether a surface passed a usable view analytics options object. Anything else leaves the
 * player silent, which is how previews and admin pages stay out of the figures.
 *
 * @param {Object} [config] - { endpoint, vk, rk, surface, channel }
 * @returns {Boolean} true when every field is present and well typed
 */
export function isViewAnalyticsConfig(config) {
  return Boolean(config)
    && isKey(config.endpoint)
    && isKey(config.vk)
    && isKey(config.rk)
    && Number.isInteger(config.surface)
    && Number.isInteger(config.channel);
}

/**
 * Reports one player session to client_service's video_views endpoint, following the tech plan's
 * flush rules. watch_ms comes from media positions, so 60s watched at 2x counts 60000.
 *
 * The session id is the wrapper's `uuid`, and a new one is minted only when `rk` changes. Quality
 * selection and error recovery change the source of the same render and keep the session.
 */
export default class ViewAnalytics {
  /**
   * @param {Object} wrapper - The Animoto Player: its video.js instance as `player`, on/off, `cta` and `uuid`
   * @param {Object} config - { endpoint, vk, rk, surface, channel }
   */
  constructor(wrapper, config) {
    this.wrapper = wrapper;
    this.config = { ...config };
    this.listeners = null;
    this.heartbeat = null;
    this.resetSession();
    // The Player calls these directly, so none of them may throw into playback.
    ['attach', 'detach', 'flush', 'recordCtaClick', 'sendLoad', 'setConfig'].forEach((method) => {
      this[method] = guard(this[method].bind(this));
    });
  }

  resetSession() {
    this.seq = 0;
    this.baseline = null;
    this.isPlaybackStarted = false;
    this.totals = {
      cta_clicks  : 0,
      furthest_ms : 0,
      plays       : 0,
      watch_ms    : 0
    };
  }

  identity() {
    const { channel, rk, surface, vk } = this.config;
    return {
      channel,
      rk,
      sid : this.wrapper.uuid,
      surface,
      v   : REPORT_VERSION,
      vk
    };
  }

  readMedia(method) {
    const media = this.wrapper.player;
    return media && typeof media[method] === 'function' ? media[method]() : undefined;
  }

  readPositionMs() {
    const seconds = this.readMedia('currentTime');
    return Number.isFinite(seconds) ? seconds * 1000 : null;
  }

  readRate() {
    const rate = this.readMedia('playbackRate');
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  setBaseline() {
    const positionMs = this.readPositionMs();
    this.baseline = positionMs === null ? null : { atMs : Date.now(), positionMs, rate : this.readRate() };
  }

  sample() {
    const positionMs = this.readPositionMs();
    // seeking() turns true when currentTime is set, before the 'seeking' event dispatches, so a
    // timeupdate queued in between cannot count the jump. 'seeking' also clears the baseline.
    if (this.readMedia('seeking') === true || positionMs === null) {
      return;
    }
    const atMs = Date.now();
    const rate = this.readRate();
    if (this.baseline) {
      this.accept(positionMs, atMs, rate);
    }
    this.baseline = { atMs, positionMs, rate };
  }

  accept(positionMs, atMs, rate) {
    // The rate may have changed inside the interval, so allow the faster of its two ends.
    const boundRate = Math.max(rate, this.baseline.rate);
    const deltaMs = positionMs - this.baseline.positionMs;
    if (deltaMs > 0 && deltaMs <= ((atMs - this.baseline.atMs) * boundRate) + SAMPLE_SLACK_MS) {
      this.totals.watch_ms += deltaMs;
      this.totals.furthest_ms = Math.max(this.totals.furthest_ms, positionMs);
    }
  }

  sendLoad() {
    const payload = { ...this.identity(), t : 'load' };
    const autoplay = this.readMedia('autoplay');
    const isMuted = this.readMedia('muted');
    if (autoplay !== undefined) {
      payload.autoplay = Boolean(autoplay); // video.js may report 'any' or 'muted'
    }
    if (typeof isMuted === 'boolean') {
      payload.muted = isMuted;
    }
    send(this.config.endpoint, payload);
  }

  flush() {
    this.sample();
    this.seq += 1;
    const payload = {
      ...this.identity(),
      cta_clicks  : this.totals.cta_clicks,
      furthest_ms : Math.round(this.totals.furthest_ms),
      plays       : this.totals.plays,
      seq         : this.seq,
      t           : 'flush',
      watch_ms    : Math.round(this.totals.watch_ms)
    };
    const durationSeconds = this.readMedia('duration');
    if (Number.isFinite(durationSeconds) && durationSeconds > 0) {
      payload.len_ms = Math.round(durationSeconds * 1000);
    }
    send(this.config.endpoint, payload);
  }

  stopHeartbeat() {
    clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  startPlayback() {
    if (!this.isPlaybackStarted) {
      this.isPlaybackStarted = true;
      this.totals.plays += 1;
    }
    this.setBaseline();
    this.flush();
    this.stopHeartbeat();
    this.heartbeat = setInterval(guard(() => this.flush()), HEARTBEAT_MS);
  }

  // Sent with the session's next flush, as the player's own CTA anchor click is.
  recordCtaClick() {
    this.totals.cta_clicks += 1;
  }

  /**
   * Subscribes to the wrapper's current video.js instance, its CTA and pagehide. Called again
   * whenever the wrapper builds a new video.js instance.
   */
  attach() {
    this.detach();
    const handlers = {
      ended : () => {
        this.stopHeartbeat();
        this.flush();
        this.isPlaybackStarted = false; // so a replay counts as a new play
      },
      loadstart : () => { this.baseline = null; }, // video.js fires this on every source change
      pause     : () => {
        this.stopHeartbeat();
        this.flush();
      },
      play   : () => this.startPlayback(),
      seeked : () => {
        this.setBaseline();
        this.flush();
      },
      ratechange : () => this.sample(), // splits the interval at the change
      // A loop's rewind is a seek too, so each loop drops the playback after its last sample.
      seeking    : () => { this.baseline = null; },
      timeupdate : () => this.sample(),
      waiting    : () => {
        this.sample(); // keeps the playback since the last timeupdate before the stall
        this.setBaseline();
      }
    };
    const events = Object.keys(handlers).map(event => [event, guard(handlers[event])]);
    const ctaElement = this.wrapper.cta && this.wrapper.cta.ctaElement;
    const onCtaClick = guard(() => this.recordCtaClick());
    const onPageHide = guard(() => this.flush());
    // Mobile browsers often fire no pagehide when the viewer switches apps and the tab is later
    // discarded. A load-only session has nothing new to send, so it stays silent on tab switches.
    const onVisibilityChange = guard(() => {
      if (document.visibilityState === 'hidden' && this.totals.plays > 0) {
        this.flush();
      }
    });

    this.listeners = {
      ctaElement, events, onCtaClick, onPageHide, onVisibilityChange
    };
    events.forEach(([event, handler]) => this.wrapper.on(event, handler));
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (ctaElement) {
      ctaElement.addEventListener('click', onCtaClick);
    }
  }

  detach() {
    this.stopHeartbeat();
    if (!this.listeners) {
      return;
    }
    const {
      ctaElement, events, onCtaClick, onPageHide, onVisibilityChange
    } = this.listeners;
    this.listeners = null;
    guard(() => events.forEach(([event, handler]) => this.wrapper.off(event, handler)))();
    window.removeEventListener('pagehide', onPageHide);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (ctaElement) {
      ctaElement.removeEventListener('click', onCtaClick);
    }
  }

  /**
   * Points reporting at another render. Only a different `rk` starts a new session: the current
   * one is flushed first, then a new session id is minted and `seq` restarts at 1.
   *
   * @param {Object} config - { endpoint, vk, rk, surface, channel }
   */
  setConfig(config) {
    if (config.rk === this.config.rk) {
      this.config = { ...config };
      return;
    }
    this.stopHeartbeat();
    this.flush();
    this.config = { ...config };
    this.wrapper.uuid = createSessionId();
    this.resetSession();
    this.sendLoad();
    // No play event follows when the page switches render mid-playback, so the new session starts playing here.
    if (this.readMedia('paused') === false) {
      this.startPlayback();
    }
  }
}
